import { useEffect, useMemo } from 'react';
import { useGetCareProgramsQuery } from 'services/careProgramService';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { healthTopicFromAPIString, getAllHealthTopicsSorted } from '../utils/healthTopicUtils';
import { transformCareProgram, groupProgramsBySections } from '../utils/careProgramUtils';
import type { CareProgramAPIResponse, HealthTopic, ProgramSection } from '../types';

export interface UseCareProgramsDataReturn {
  careProgramsData: CareProgramAPIResponse[] | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  healthTopics: HealthTopic[];
  groupedProgramsByTopicMap: Record<HealthTopic, ProgramSection[]>;
}

/**
 * Fetches care programs for the logged-in member and groups them into
 * per-topic sections. Pure data layer — no URL, navigation, or device-type
 * concerns; screen hooks compose this with useCareProgramModalRoute and
 * useCareProgramEnrollment.
 */
export const useCareProgramsData = (): UseCareProgramsDataReturn => {
  const user = useLoggedInUser();
  const { currentLocale, translation } = useTranslationContext();
  const healthTopics = getAllHealthTopicsSorted();
  const memberUuid = user?.uuid;

  const {
    data: careProgramsData,
    isLoading,
    error,
    refetch,
  } = useGetCareProgramsQuery(
    { memberUuid: memberUuid ?? '', locale: 'en' },
    { skip: !memberUuid },
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetch();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refetch]);

  const groupedProgramsByTopicMap = useMemo(() => {
    const sectionsByTopic = {} as Record<HealthTopic, ProgramSection[]>;

    healthTopics.forEach((topic) => {
      if (!careProgramsData) {
        sectionsByTopic[topic] = [];
        return;
      }

      const topicPrograms = careProgramsData
        .filter(
          (program) =>
            program.care_program_mappings?.is_member_visible &&
            program.care_program_mappings?.health_topics?.some(
              (apiTopic) => healthTopicFromAPIString(apiTopic) === topic,
            ),
        )
        .map(transformCareProgram)
        .filter((program): program is NonNullable<typeof program> => program !== null);

      sectionsByTopic[topic] = groupProgramsBySections(topicPrograms, topic, translation);
    });

    return sectionsByTopic;
  }, [careProgramsData, healthTopics, translation]);

  return {
    careProgramsData,
    isLoading,
    error,
    refetch,
    healthTopics,
    groupedProgramsByTopicMap,
  };
};
